<script setup lang="ts">
import { useQuantitySuggestion } from '@/composables/quantity-suggestion'
import useCartStore from '@/stores/cart'
import useProductStore from '@/stores/product'
import Icon from 'lilasia-icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { getQuantity } = useQuantitySuggestion()
const { t } = useI18n()
const productStore = useProductStore()
const cartStore = useCartStore()

const addedToCartQuantity = computed(() => {
  return cartStore.productExistedInCart?.quantity || 0
})

const applied = computed(() => {
  return recommendedQuantity.value === cartStore.quantity
})

const recommendedQuantity = computed(() => {
  const weight = productStore.selectedProduct?.weight
  const quantity = cartStore.quantity + addedToCartQuantity.value

  if (weight) {
    return getQuantity(weight, quantity) - addedToCartQuantity.value
  }

  return cartStore.quantity
})

const message = computed(() => {
  const remainingQuantity = recommendedQuantity.value - cartStore.quantity

  return applied.value
    ? 'Enjoy the savings on your order!'
    : addedToCartQuantity.value > 0
      ? `Save more on shipping by buying ${remainingQuantity} more.`
      : `Save more on shipping by buying ${recommendedQuantity.value}.`
})

const buttonText = computed(() => {
  return applied.value ? t('applied') : t('apply')
})

const apply = () => {
  cartStore.quantity = recommendedQuantity.value
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="recommendedQuantity > cartStore.quantity || applied">
      <div
        class="flex flex-row items-center justify-between gap-16 rounded-8 border border-black-10 p-16 md:flex-col lg:flex-row"
      >
        <div class="flex items-center gap-8 text-black-60">
          <Icon
            name="percent"
            :size="24"
          />
          <span class="text-16 leading-20">
            {{ message }}
          </span>
        </div>

        <button
          class="rounded-8 bg-blue-80 px-16 py-8 text-16 leading-20 text-white transition hover:bg-blue-100 disabled:bg-black-10 md:w-full lg:w-auto"
          :disabled="applied"
          @click="apply"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </Transition>
</template>
